import React, { useState, ReactNode } from 'react';
import { PromiseState } from 'react-refetch';
import { Helmet } from 'react-helmet';
import styled from 'styled-components/macro';

import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import useMedia from 'hooks/useMedia';

import BaseBox from '../elements/Box';
import ScrollToTopOnMount from 'components/utility/ScrollTopOnMount/ScrollTopOnMount';
import TopBar from 'components/Header/TopBar/TopBar';
import Header from 'components/Header/Header';
import Layout from 'components/styled/Layout/Layout';
import CustomListHeader from 'components/customlists/CustomListHeader';
import InViewObserver from 'components/utility/InViewObserver/InViewObserver';
import { HeaderContainer } from 'components/Header/EntityHeader/EntityHeader.style';
import ManageListDropdown from 'components/customlists/ManageListDropdown';
import { OutlinedOpenPopupButton } from 'components/notifications/Settings/styles';
import AddToMyListsButton from './AddToMyListsButton';
import CustomListModal from './CustomListModal';

import { isInMyLists } from 'redux/reducers/myLists';
import { isUserCuratorForList } from 'components/customlists/utils/isUserCuratorForList';

import { TTableHeader } from '../CustomizeView/CustomizeView';
import { ICustomList, ICustomListData } from './AddToList/shared';

const Box = styled(BaseBox)`
  display: flex;
  justify-content: center;

  @media screen and (min-width: 40em) {
    justify-content: normal;
  }
`;

interface ICustomListPageHeaderProps {
  customList?: ICustomList;
  customListInvitationData: PromiseState<any>;
  customListModalMetadata: PromiseState<ICustomList>;
  deleteCustomEntityList: (listId: string, close: () => void) => any;
  fetchCustomListInvitation: ({ listId }: { listId: string }) => any;
  leaveCustomEntityList: (
    listId: string,
    userId: string,
    close: () => void
  ) => any;
  loading: boolean;
  putCustomEntityList: (list: ICustomListData, close: () => void) => any;
  saveToApi: (headers: TTableHeader[]) => void;
  userId: string;
}

export type TModalContent =
  | 'EDIT_LIST'
  | 'DELETE_LIST'
  | 'SHARE_LIST'
  | 'LEAVE_LIST';

const CustomListPageHeader: React.FC<ICustomListPageHeaderProps> = (props) => {
  const {
    customList,
    customListInvitationData,
    customListModalMetadata,
    deleteCustomEntityList,
    fetchCustomListInvitation,
    loading,
    leaveCustomEntityList,
    putCustomEntityList,
    saveToApi,
    userId,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState<TModalContent>('EDIT_LIST');
  const location = useLocation();
  const isEditRoute = location.pathname.endsWith('edit');
  const isCurator = isUserCuratorForList({ customList, userId });
  const isSmallScreen = useMedia('(max-width: 36em)');
  const lastUpdated = customList?.lastUpdatedAt
    ? new Date(customList?.lastUpdatedAt)
    : new Date();
  const title = customList?.displayName ?? '';
  const thumbnailUrl = customList?.thumbnailUrl ?? '';
  const listId = customList?.id ?? '';

  const openModal = (): void => {
    setShowModal(true);
  };

  const hideModal = (): void => {
    setShowModal(false);
  };

  const editList = (): void => {
    setModalContent('EDIT_LIST');
    openModal();
  };

  const shareList = (): void => {
    if (isCurator) {
      fetchCustomListInvitation({
        listId: (customList as ICustomList).id,
      });
    }
    setModalContent('SHARE_LIST');
    openModal();
  };

  const deleteList = (): void => {
    setModalContent('DELETE_LIST');
    openModal();
  };

  const leaveList = (): void => {
    setModalContent('LEAVE_LIST');
    openModal();
  };

  const listHasAlreadyBeenAdded = useSelector(isInMyLists(listId));

  return (
    <>
      <Helmet defer={false} title={title} />
      <ScrollToTopOnMount />
      <InViewObserver
        render={(props: any): ReactNode => (
          <Header hasStickyTopBar>
            <TopBar
              topicEntityType="list"
              entityTitle={title}
              inView={props.inview}
              isSticky
              artworkSrc={thumbnailUrl}
            />
            <Layout.MaxWidth>
              <HeaderContainer loading={loading}>
                <CustomListHeader
                  thumbnailUrl={thumbnailUrl}
                  title={title}
                  isCurator={isCurator}
                  isSmallScreen={isSmallScreen}
                  lastUpdated={lastUpdated}
                >
                  {!isEditRoute && (
                    <Box
                      display="flex"
                      justifyContent={['center', 'center', 'normal']}
                      lineHeight="1.8"
                    >
                      {!isCurator && (
                        <AddToMyListsButton
                          isInMyLists={listHasAlreadyBeenAdded}
                          listId={listId}
                        />
                      )}
                      {isCurator && (
                        <ManageListDropdown
                          items={[
                            {
                              label: 'Edit list',
                              link: `/list/${listId}/edit`,
                            },
                            {
                              label: 'List information',
                              action: editList,
                            },
                            {
                              label: 'Leave list',
                              action: leaveList,
                            },
                            {
                              label: 'Delete list',
                              action: deleteList,
                            },
                          ]}
                          defaultLabel="Manage list"
                          disabled={!listId}
                        />
                      )}
                      {customList && (
                        <OutlinedOpenPopupButton
                          onClick={shareList}
                          ml={isCurator ? null : 0}
                        >
                          Share
                        </OutlinedOpenPopupButton>
                      )}
                    </Box>
                  )}
                </CustomListHeader>
              </HeaderContainer>
            </Layout.MaxWidth>
          </Header>
        )}
      />
      {showModal && customList && (
        <CustomListModal
          customList={customList}
          customListInvitationData={customListInvitationData}
          customListMetadata={customListModalMetadata}
          deleteCustomEntityList={deleteCustomEntityList}
          fetchCustomListInvitation={fetchCustomListInvitation}
          hideModal={hideModal}
          leaveCustomEntityList={leaveCustomEntityList}
          modalContent={modalContent}
          putCustomEntityList={putCustomEntityList}
          saveToApi={saveToApi}
          userId={userId}
        />
      )}
    </>
  );
};

export default CustomListPageHeader;
